import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getAllJobs } from '../data/jobs';
import JobCard from '../components/JobCard';
import SEO from '../components/SEO';
import './CategoryJobsPage.css';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();

  const results = useMemo(() => {
    if (!query) return [];

    // Split query into individual words for fuzzy matching
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);

    const scored = getAllJobs().map(job => {
      const searchable = [
        job.title,
        job.organization,
        job.location,
        job.description,
        job.salary,
        ...(job.eligibility || []),
        ...(job.categories || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      let score = 0;

      // Exact full-query match gets highest boost
      if (searchable.includes(query.toLowerCase())) {
        score += 100;
      }

      // Score each word: exact word match, partial/substring match
      for (const word of words) {
        if (!word) continue;
        // Exact word boundary match
        if (new RegExp('\\b' + word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b').test(searchable)) {
          score += 20;
        }
        // Substring match (partial/fuzzy)
        else if (searchable.includes(word)) {
          score += 10;
        }
        // Prefix match (first 3+ chars match start of any word in text)
        else if (word.length >= 3 && new RegExp('\\b' + word.slice(0, 3).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).test(searchable)) {
          score += 3;
        }
      }

      // Title match gets extra weight
      const titleLower = (job.title || '').toLowerCase();
      for (const word of words) {
        if (titleLower.includes(word)) score += 15;
      }

      return { job, score };
    });

    return scored
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(item => item.job);
  }, [query]);

  return (
    <div className="category-jobs-page">
      <SEO
        title={query ? `Search: ${query}` : 'Search Jobs'}
        description={`Search results for "${query}" – Sarkari Naukri Rajasthan`}
        path={`/search?q=${encodeURIComponent(query)}`}
        noIndex
      />

      <nav className="breadcrumb-bar">
        <Link to="/">Home</Link> <span className="sep">›</span> Search Results
      </nav>

      <h1 className="category-title">
        {query ? `Search Results for "${query}"` : 'Search Jobs'}
      </h1>

      {query && (
        <p className="category-count">
          {results.length} job{results.length !== 1 ? 's' : ''} found
        </p>
      )}

      {!query && <p className="category-count">Enter a keyword to search for jobs.</p>}

      {query && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
          <p style={{ fontSize: '1.1rem' }}>No jobs found matching "<strong>{query}</strong>"</p>
          <p style={{ marginTop: 8 }}>Try different keywords like "Railway", "Bank", "SSC", "10th pass", etc.</p>
        </div>
      )}

      <div className="job-list">
        {results.map(job => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>
    </div>
  );
};

export default SearchResultsPage;
